import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { apiFetch } from '../../api';
import { useSettings, formatNumber } from '../../context/SettingsContext';
import { Card } from '../ui';

// ── Tokens ─────────────────────────────────────────────────────────────────────
const SAGE = '#6F8F7E';
const SAGE_DEEP = '#4F6F5F';
const ROSE = '#C18B7E';
const ROSE_DEEP = '#8B5249';
const INK_100 = '#EAEAE2';
const INK_300 = '#A1A1AA';

// ── Types ──────────────────────────────────────────────────────────────────────

interface MonthData {
  month: string;
  total_payout: number;
  rental_count: number;
  vehicle_count: number;
}

interface Summary {
  total_payout: number;
  ytd_payout: number;
  total_rentals: number;
  ytd_rentals: number;
}

interface ApiResponse {
  months: MonthData[];
  summary: Summary;
}

// Hvad Recharts sender til vores custom Tooltip-content (kun det vi bruger).
type ChartRow = MonthData & { label: string; labelShort: string };
interface TooltipEntry {
  dataKey?: string;
  value?: number;
  payload?: ChartRow;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function getLast12Months(): string[] {
  const result: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return result;
}

function formatMonthLabel(month: string, lang: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const locale = lang === 'da' ? 'da-DK' : 'en-GB';
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

function formatMonthShort(month: string, lang: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const locale = lang === 'da' ? 'da-DK' : 'en-GB';
  return d.toLocaleDateString(locale, { month: 'short' });
}

function compactAmount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

// ── Custom tooltip ─────────────────────────────────────────────────────────────

function RevenueTooltip({
  active,
  payload,
  currency,
  numberFormat,
  t,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  currency: string;
  numberFormat: 'dk' | 'us';
  t: (key: string) => string;
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;
  const revenueEntry = payload.find(p => p.dataKey === 'total_payout');
  const rentalEntry  = payload.find(p => p.dataKey === 'rental_count');

  return (
    <div className="bg-cream-50 border border-ink-100 rounded-lg px-3.5 py-2.5 text-xs shadow-card-md min-w-[200px]">
      <p className="text-ink-500 font-medium mb-2 text-[11px] tracking-wider uppercase">{row.label}</p>
      {revenueEntry != null && (
        <p className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full bg-sage-500" />
          <span className="text-ink-900 font-semibold">
            {currency} {formatNumber(revenueEntry.value ?? 0, numberFormat, 0)}
          </span>
          <span className="text-ink-500">{t('dashboard.revenue')}</span>
        </p>
      )}
      {rentalEntry != null && (
        <p className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />
          <span className="text-ink-900 font-semibold">{rentalEntry.value}</span>
          <span className="text-ink-500">{t('dashboard.rentals_label')}</span>
          <span className="text-ink-300">·</span>
          <span className="text-ink-500">{row.vehicle_count} {t('dashboard.vehicles')}</span>
        </p>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function RevenueStatsWidget() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();

  const [data, setData]       = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboard/revenue-stats')
      .then(r => r.json())
      .then((d: ApiResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const chartData = (() => {
    if (!data) return [];
    const map = new Map(data.months.map(m => [m.month, m]));
    return getLast12Months().map(month => {
      const row = map.get(month);
      return {
        month,
        label: formatMonthLabel(month, i18n.language),
        labelShort: formatMonthShort(month, i18n.language),
        total_payout:  row?.total_payout  ?? 0,
        rental_count:  row?.rental_count  ?? 0,
        vehicle_count: row?.vehicle_count ?? 0,
      };
    });
  })();

  const summary = data?.summary;

  return (
    <Card padded>
      <div className="flex items-baseline justify-between mb-5">
        <h3 className="text-base font-semibold text-ink-900 tracking-tight">
          {t('dashboard.stats_title')}
        </h3>
        <span className="text-xs text-ink-500">12 mdr.</span>
      </div>

      {loading && (
        <p className="text-sm text-ink-500">{t('dashboard.loading')}</p>
      )}
      {error && (
        <p className="text-sm text-rose-700">{t('dashboard.error')}</p>
      )}

      {!loading && !error && (
        <>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={INK_100} vertical={false} />

              <XAxis
                dataKey="labelShort"
                tick={{ fill: INK_300, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
              />

              <YAxis
                yAxisId="revenue"
                orientation="left"
                tickFormatter={compactAmount}
                tick={{ fill: INK_300, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={44}
              />

              <YAxis
                yAxisId="count"
                orientation="right"
                tick={{ fill: INK_300, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={28}
                allowDecimals={false}
              />

              <Tooltip
                content={
                  <RevenueTooltip
                    currency={settings.currency}
                    numberFormat={settings.numberFormat}
                    t={t}
                  />
                }
                cursor={{ stroke: INK_100, strokeWidth: 1 }}
              />

              {/* Omsætning — sage */}
              <Line
                yAxisId="revenue"
                type="monotone"
                dataKey="total_payout"
                stroke={SAGE}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: SAGE, stroke: '#FFFFFF', strokeWidth: 2 }}
              />

              {/* Udlejninger — rose */}
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="rental_count"
                stroke={ROSE}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{ r: 5, fill: ROSE, stroke: '#FFFFFF', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="flex items-center gap-5 text-xs text-ink-500 mt-3">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded" style={{ background: SAGE }} />
              {t('dashboard.revenue')}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded border-t border-dashed" style={{ borderColor: ROSE }} />
              {t('dashboard.rentals_label')}
            </span>
          </div>

          {/* Summary */}
          {summary && (
            <div className="border-t border-ink-100 pt-4 mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div>
                <div className="text-ink-500 mb-1">{t('dashboard.revenue')}</div>
                <div className="font-semibold text-ink-900 text-sm">
                  {settings.currency} {formatNumber(summary.total_payout, settings.numberFormat, 0)}
                  <span className="text-ink-300 font-normal ml-1.5">{t('dashboard.total_label')}</span>
                </div>
                <div className="text-ink-500 mt-0.5" style={{ color: SAGE_DEEP }}>
                  {settings.currency} {formatNumber(summary.ytd_payout, settings.numberFormat, 0)} {t('dashboard.ytd_label')}
                </div>
              </div>
              <div>
                <div className="text-ink-500 mb-1">{t('dashboard.rentals_label')}</div>
                <div className="font-semibold text-ink-900 text-sm">
                  {summary.total_rentals}
                  <span className="text-ink-300 font-normal ml-1.5">{t('dashboard.total_label')}</span>
                </div>
                <div className="mt-0.5" style={{ color: ROSE_DEEP }}>
                  {summary.ytd_rentals} {t('dashboard.ytd_label')}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
